import { useEffect, useRef, useCallback } from "react";
import { clientRole } from "../lib/roles";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { safeOnSnapshot } from "../lib/safeSnapshot";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";

export const useClientNotifications = () => {
  const { user, loading: authLoading } = useAuth();
  const {
    updateClientUnreadCount,
    updateClientBudgetCount,
    updateClientBudgetReadyCount,
    updateClientDivergenceCount,
    updateClientAnalysisCount,
    updateClientGoingOnCount,
    updateClientPaymentsCount,
    updateClientDraftsCount,
    updateClientCompletedCount,
    updateClientRefundCount,
  } = useNotifications();

  // Referencias para evitar re-renders desnecessários
  const countersRef = useRef({
    unread: new Set(),
    budget: new Set(),
    budgetReady: new Set(),
    divergence: new Set(),
    analysis: new Set(),
    goingOn: new Set(),
    payments: new Set(),
    drafts: new Set(),
    completed: new Set(),
    refund: new Set(),
  });

  const unsubscribeFunctionsRef = useRef([]);
  const isInitializedRef = useRef(false);
  const timeoutRef = useRef(null);

  // Debounce para atualizações
  const updateCounters = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const counters = countersRef.current;
      updateClientUnreadCount(counters.unread.size);
      updateClientBudgetCount(counters.budget.size);
      updateClientBudgetReadyCount(counters.budgetReady.size);
      updateClientDivergenceCount(counters.divergence.size);
      updateClientAnalysisCount(counters.analysis.size);
      updateClientGoingOnCount(counters.goingOn.size);
      updateClientPaymentsCount(counters.payments.size);
      updateClientDraftsCount(counters.drafts.size);
      updateClientCompletedCount(counters.completed.size);
      updateClientRefundCount(counters.refund.size);
    }, 100); // Debounce de 100ms
  }, [
    updateClientUnreadCount,
    updateClientBudgetCount,
    updateClientBudgetReadyCount,
    updateClientDivergenceCount,
    updateClientAnalysisCount,
    updateClientGoingOnCount,
    updateClientPaymentsCount,
    updateClientDraftsCount,
    updateClientCompletedCount,
    updateClientRefundCount,
  ]);

  useEffect(() => {
    if (authLoading || !user || isInitializedRef.current) {
      return;
    }

    // Papel de cliente efetivo (cobre userType string OU array multi-papel).
    // Fallback null: quem não tem papel de cliente não passa no filtro abaixo.
    const userType = clientRole(user?.userType, null);

    // Só executar para usuários cliente
    if (!["b2b", "b2c", "colab"].includes(userType)) {
      return;
    }

    isInitializedRef.current = true;

    const fetchAllNotifications = async () => {
      try {
        const firestore = getFirestore();

        // Buscar dados do usuário
        const userDoc = await getDocs(
          query(
            collection(firestore, "users"),
            where("email", "==", user.email)
          )
        );

        if (userDoc.empty) {
          console.error("Documento do usuário não encontrado");
          return;
        }

        const userData = userDoc.docs[0].data();
        const userRegisteredBy = userData.registeredBy;
        const registeredByType = userData.registeredByType;
        const colaboradores = userData.colaboradores || [];
        const projectPermissions = userData.projectPermissions || [];

        // Array para armazenar os emails dos projetos a serem buscados
        let emailsToSearch = [];

        // Se for colab, busca apenas os projetos do próprio usuário e dos usuários que ele tem permissão
        if (userType === "colab") {
          emailsToSearch = [user.email, ...projectPermissions];
        } else {
          // Para b2b/b2c, busca projetos do usuário e dos vinculados
          const usersWithSameRegisteredBy = query(
            collection(firestore, "users"),
            where("registeredBy", "==", userRegisteredBy || user.email)
          );
          const usersSnapshot = await getDocs(usersWithSameRegisteredBy);
          emailsToSearch = usersSnapshot.docs
            .map((doc) => doc.data().email)
            .filter((email) => email);

          // Adicionar o email do usuário atual à lista se ele não estiver incluído
          if (user.email && !emailsToSearch.includes(user.email)) {
            emailsToSearch.push(user.email);
          }

          // Adicionar os emails dos colaboradores
          colaboradores.forEach((colab) => {
            if (colab.email && !emailsToSearch.includes(colab.email)) {
              emailsToSearch.push(colab.email);
            }
          });
        }

        // Coleções que este usuário pode enxergar. Colab vê apenas o tipo de
        // quem o registrou; b2b/b2c veem as 10 coleções de projetos (mesma
        // lista de ClientProjects.js).
        let allowed;
        if (userType === "colab") {
          allowed =
            registeredByType === "b2b"
              ? [
                  "b2bdocsaved",
                  "b2bdocprojects",
                  "b2bsketch",
                  "b2bapproved",
                  "b2bapproval",
                  "b2bprojectspaid",
                ]
              : [
                  "b2csketch",
                  "b2cdocsaved",
                  "b2cdocprojects",
                  "b2cprojectspaid",
                ];
        } else {
          allowed = [
            "b2bdocsaved",
            "b2bdocprojects",
            "b2bsketch",
            "b2bapproved",
            "b2bapproval",
            "b2bprojectspaid",
            "b2csketch",
            "b2cdocsaved",
            "b2cdocprojects",
            "b2cprojectspaid",
          ];
        }
        const allowedSet = new Set(allowed);

        // Lê um status que pode vir como string OU objeto { status }.
        const statusStr = (v) =>
          String((v && typeof v === "object" ? v.status : v) || "");

        // Cada categoria = coleções + predicado que espelha EXATAMENTE o filtro
        // da tabela da página correspondente. Todas contam apenas NÃO LIDOS
        // (ClientRead === false), aplicado de forma uniforme no loop abaixo.
        const CATEGORY_DEFS = [
          {
            name: "budget", // ClientBudget.js
            cols: ["b2bdocprojects", "b2cdocprojects"],
            test: (d) => statusStr(d.project_status) === "Ag. Orçamento",
          },
          {
            name: "budgetReady", // ClientBudgetReady.js
            cols: ["b2bapproval", "b2csketch"],
            test: (d) =>
              ["Orçamento Recebido", "Ag. Pagamento", "Ag. Aprovação"].includes(
                statusStr(d.project_status)
              ),
          },
          {
            name: "analysis", // ClientAnalysis.js
            cols: ["b2bapproved", "b2bprojectspaid", "b2cprojectspaid"],
            test: (d) =>
              statusStr(d.project_status).toLowerCase() === "em análise",
          },
          {
            name: "goingOn", // ClientGoingOn.js
            cols: ["b2bprojectspaid", "b2bapproved", "b2cprojectspaid"],
            test: (d) => statusStr(d.translation_status) === "Em Tradução",
          },
          {
            name: "divergence", // ClientDivergence.js
            cols: [
              "b2bprojectspaid",
              "b2cprojectspaid",
              "b2bapproval",
              "b2bdocprojects",
              "b2bapproved",
            ],
            test: (d) =>
              ["Em Divergência", "Divergência"].includes(
                statusStr(d.payment_status)
              ),
          },
          {
            name: "payments", // ClientPayments.js
            cols: allowed,
            test: (d) => statusStr(d.payment_status) === "Pendente",
          },
          {
            name: "drafts", // ClientDrafts.js
            cols: ["b2bsketch", "b2csketch", "b2bdocsaved", "b2cdocsaved"],
            test: (d) => statusStr(d.project_status) === "Rascunho",
          },
          {
            name: "completed", // ClientProjectsDone.js
            cols: ["b2bprojectspaid", "b2bapproved", "b2cprojectspaid"],
            test: (d) =>
              ["Finalizado", "Concluído"].includes(
                statusStr(d.translation_status)
              ) || statusStr(d.project_status) === "Finalizado",
          },
          {
            name: "refund", // ClientRefund.js
            cols: ["b2bprojectspaid", "b2cprojectspaid"],
            test: (d) =>
              ["Em Reembolso", "Reembolsado"].includes(
                statusStr(d.payment_status)
              ),
          },
        ];

        // Restringe cada categoria às coleções que este usuário pode ver.
        const cats = CATEGORY_DEFS.map((c) => ({
          name: c.name,
          test: c.test,
          cols: c.cols.filter((x) => allowedSet.has(x)),
        }));

        // Reset dos contadores.
        countersRef.current = {
          unread: new Set(),
          budget: new Set(),
          budgetReady: new Set(),
          divergence: new Set(),
          analysis: new Set(),
          goingOn: new Set(),
          payments: new Set(),
          drafts: new Set(),
          completed: new Set(),
          refund: new Set(),
        };

        // Contribuição de cada assinatura para cada contador. O total é a UNIÃO
        // dessas contribuições, recomputada a cada snapshot — assim, quando um
        // projeto muda de status e sai da categoria, seu id some do total em vez
        // de ficar preso para sempre (bug do "1 a mais": antes as queries eram
        // filtradas por status e o doc que saía nunca era removido do Set).
        const contrib = { unread: {} };
        cats.forEach((c) => {
          contrib[c.name] = {};
        });
        const recomputeUnion = (name) => {
          const union = new Set();
          Object.values(contrib[name]).forEach((set) =>
            set.forEach((id) => union.add(id))
          );
          countersRef.current[name] = union;
        };

        // UMA assinatura por (coleção, e-mail, campo). A query filtra só por
        // dono (projectOwner/userEmail/registeredBy) — SEM status. O filtro de
        // categoria é feito em JS, para o doc permanecer no snapshot ao mudar de
        // status e poder ser removido do total. registeredBy alimenta apenas
        // "Todos Projetos" (como faz ClientProjects.js); as categorias usam só
        // projectOwner/userEmail, como as respectivas páginas.
        allowed.forEach((col) => {
          emailsToSearch.forEach((email) => {
            ["projectOwner", "userEmail", "registeredBy"].forEach((field) => {
              const subKey = `${col}|${email}|${field}`;
              const q = query(
                collection(firestore, col),
                where(field, "==", email)
              );

              const unsubscribe = safeOnSnapshot(q, (snapshot) => {
                const docs = snapshot.docs;

                // Todos Projetos (não lidos) — inclui registeredBy.
                const unreadSet = new Set();
                docs.forEach((doc) => {
                  if (doc.data().ClientRead === false) unreadSet.add(doc.id);
                });
                contrib.unread[subKey] = unreadSet;
                recomputeUnion("unread");

                // Categorias: só projectOwner/userEmail (espelha as páginas).
                if (field !== "registeredBy") {
                  cats.forEach((cat) => {
                    if (!cat.cols.includes(col)) return;
                    const s = new Set();
                    docs.forEach((doc) => {
                      const d = doc.data();
                      if (d.ClientRead === false && cat.test(d)) s.add(doc.id);
                    });
                    contrib[cat.name][subKey] = s;
                    recomputeUnion(cat.name);
                  });
                }

                updateCounters();
              }, { label: subKey });

              unsubscribeFunctionsRef.current.push(unsubscribe);
            });
          });
        });
      } catch (error) {
        console.error("Erro ao buscar notificações do cliente:", error);
      }
    };

    fetchAllNotifications();

    // Cleanup function
    return () => {
      unsubscribeFunctionsRef.current.forEach((unsubscribe) => {
        if (unsubscribe) unsubscribe();
      });
      unsubscribeFunctionsRef.current = [];
      isInitializedRef.current = false;
    };
  }, [user, authLoading, updateCounters]);
};
